import { useState, useCallback, useRef, useEffect } from 'react';
import authService from '../services/authService';

const PREDICTION_COLORS = [
  'rgba(253, 186, 116, 0.45)',
  'rgba(249, 115, 22,  0.50)',
  'rgba(234, 88,  12,  0.55)',
  'rgba(220, 38,  38,  0.60)',
  'rgba(185, 28,  28,  0.65)',
];

// ── "Premier contour" (date_first_outline) — le polygone `main` représente
// ce contour initial. On le colore désormais en amont du dégradé des
// prédictions (couleur plus pâle que PREDICTION_COLORS[0]) plutôt qu'avec
// une couleur fixe à part, pour qu'il se lise visuellement comme le point
// de départ de la progression plutôt que comme un calque isolé.
const FIRST_OUTLINE_FILL    = 'rgba(254, 215, 170, 0.35)';
const FIRST_OUTLINE_OUTLINE = '#fdba74';

const POINT_COLOR  = '#f97316';

const DEFAULT_FILTERS = { reference: '', dateFrom: '', dateTo: '' };

// ── Helpers carte ────────────────────────────────────────────────

const clearSimLayers = (map) => {
  if (!map) return;
  try {
    const style = map.getStyle?.();
    if (!style) return;
    [...(style.layers || [])]
      .filter((l) => l.id.startsWith('sim-'))
      .forEach((l) => { if (map.getLayer(l.id)) map.removeLayer(l.id); });
    Object.keys(style.sources || {})
      .filter((s) => s.startsWith('sim-'))
      .forEach((s) => { if (map.getSource(s)) map.removeSource(s); });
  } catch (e) {
    console.warn('clearSimLayers:', e);
  }
};

const addPolygonLayer = (map, id, geojson, fillColor, outlineColor = 'rgba(255,255,255,0.3)') => {
  if (!map || !geojson) return;
  try {
    if (map.getSource(id)) { map.getSource(id).setData(geojson); }
    else { map.addSource(id, { type: 'geojson', data: geojson }); }
    if (!map.getLayer(`${id}-fill`)) {
      map.addLayer({
        id: `${id}-fill`, type: 'fill', source: id,
        paint: { 'fill-color': fillColor, 'fill-opacity': 1, 'fill-antialias': true },
      });
    }
    if (!map.getLayer(`${id}-outline`)) {
      map.addLayer({
        id: `${id}-outline`, type: 'line', source: id,
        paint: { 'line-color': outlineColor, 'line-width': 1.5, 'line-opacity': 0.7 },
      });
    }
  } catch (e) { console.warn('addPolygonLayer:', e); }
};

const addPointLayer = (map, id, geojson) => {
  if (!map || !geojson) return;
  try {
    if (map.getSource(id)) { map.getSource(id).setData(geojson); }
    else { map.addSource(id, { type: 'geojson', data: geojson }); }
    if (!map.getLayer(`${id}-circle`)) {
      map.addLayer({
        id: `${id}-circle`, type: 'circle', source: id,
        paint: {
          'circle-radius': 6, 'circle-color': POINT_COLOR,
          'circle-stroke-color': '#fff', 'circle-stroke-width': 2,
          // Base explicite pour permettre le fondu via setPaintProperty dans handleStepChange.
          'circle-opacity': 1, 'circle-stroke-opacity': 1,
        },
      });
    }
  } catch (e) { console.warn('addPointLayer:', e); }
};

const getBounds = (featureCollections) => {
  let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
  const walk = (coords) => {
    if (typeof coords[0] === 'number') {
      minLng = Math.min(minLng, coords[0]); minLat = Math.min(minLat, coords[1]);
      maxLng = Math.max(maxLng, coords[0]); maxLat = Math.max(maxLat, coords[1]);
    } else { coords.forEach(walk); }
  };
  featureCollections.forEach((fc) => fc?.features?.forEach((f) => {
    if (f?.geometry?.coordinates) walk(f.geometry.coordinates);
  }));
  return isFinite(minLng) ? [[minLng, minLat], [maxLng, maxLat]] : null;
};

// ── Helper export ─────────────────────────────────────────────────

const triggerGeoJSONDownload = (geojson, filename) => {
  const blob = new Blob([JSON.stringify(geojson, null, 2)], { type: 'application/geo+json' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const slugifyName = (str) => String(str ?? '')
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .toLowerCase().trim()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

// Date locale JJ/MM/AAAA (sep = '-' pour un nom de fichier, '/' interdit)
const formatDateFR = (value, sep = '/') => {
  const d = value ? new Date(value) : new Date();
  const date = Number.isNaN(d.getTime()) ? new Date() : d;
  const pad = (n) => String(n).padStart(2, '0');
  return [pad(date.getDate()), pad(date.getMonth() + 1), date.getFullYear()].join(sep);
};

// Nom de l'utilisateur qui a lancé la simulation (plusieurs formes possibles de l'API)
const getSimulationAuthor = (sim) => {
  const u = sim?.user ?? sim?.fire?.user ?? sim?.fire?.owner ?? null;
  if (typeof u === 'string') return u;
  return (
    u?.username ?? u?.full_name ?? u?.name ??
    sim?.username ?? sim?.user_name ??
    sim?.fire?.username ?? sim?.fire?.user_name ?? sim?.fire?.created_by ??
    ''
  );
};

// ════════════════════════════════════════════════════════════════════════════════
//
// v10 — Séparation complète "Mon historique" vs "Historique d'équipe" (2 caches,
//   2 jeux de filtres indépendants, garde anti-race par version de filtre).
//
// v11 — loadTeamSimsBulk : un seul appel bulk (getFiresByUsers) au lieu du N+1
//   (une requête par user de l'arbre) pour le préchargement d'élagage équipe.
//
// v12 — loadTeamStatsBulk : idem pour statsByUser, en un seul appel bulk sans
//   filtres, au lieu d'un forEach loadUserStats qui loggait un 404 par user vide.
//
// v13 — Ordonnancement garanti des polygones `predictions` par `order` croissant
//   avant stockage dans polygonDataRef (le backend ne garantit pas l'ordre).
//
// v14 — Affichage cumulatif des polygones de simulation :
//   - `main` (contour initial) et `start` (point de départ) sont désormais
//     TOUJOURS affichés à pleine opacité, indépendamment du step actif — ce
//     ne sont plus des "étapes" du slider, juste un repère de fond permanent.
//   - Le slider est cumulatif : tous les polygones `predictions` d'index
//     <= activeStep restent visibles (pas seulement le polygone de l'étape
//     courante).
//   - Ordre de DESSIN (drawOrder) distinct de l'ordre logique
//     (polygonDataRef) : predictions d'abord, puis main, puis start — pour
//     que main/start restent visibles au-dessus même quand les prédictions
//     grandissent et finissent par les recouvrir géographiquement.
//
// v15 — Fix course critique sur le premier chargement de "Mon historique" /
//   "Historique d'équipe" : la version de filtre n'est plus incrémentée par
//   un effet lié au rendu (déclenché même sans changement réel de dépendance,
//   y compris sous StrictMode), mais UNIQUEMENT et de façon synchrone dans
//   les callbacks update*/reset* déclenchés par une vraie action utilisateur.
//
// v16 — État initial d'une simulation sélectionnée : affiche désormais la
//   simulation COMPLÈTE (dernier polygone de prédiction) plutôt que l'état
//   vide (main + start seuls, activeStep=-1). L'utilisateur peut ensuite
//   reculer dans le slider pour revoir la progression depuis le début.
//   activeStep par défaut passe donc de -1 à "dernier index disponible",
//   calculé dynamiquement une fois les polygones de la simulation chargés
//   (le nombre de prédictions varie d'une simulation à l'autre, donc cette
//   valeur ne peut pas être une constante figée au niveau du hook).
//
// v17 — Nommage de l'export GeoJSON :
//   simulation-<nom simulation>-<utilisateur qui l'a lancée>-<JJ-MM-AAAA>.geojson
//   La date est celle de la simulation (fire.created_at), au format français,
//   avec des tirets dans le nom de fichier ('/' interdit). Dans le contenu
//   (metadata.simulation_date) elle est écrite JJ/MM/AAAA.
//
// ════════════════════════════════════════════════════════════════════════════════

const useSimulations = ({ mapRef }) => {
  // ── Caches indépendants ────────────────────────────────────────────────
  const [simsByUser, setSimsByUser]     = useState({}); // équipe (fires_by_user)
  const [mySimsByUser, setMySimsByUser] = useState({}); // mon historique (get_myfires)
  const [statsByUser, setStatsByUser]   = useState({}); // cache global, non filtré, pour les stats du haut

  const [selectedSim, setSelectedSim]       = useState(null);
  // État initial par défaut avant tout chargement : -1 (rien affiché).
  // Dès qu'une simulation est chargée, ce state est réajusté au DERNIER
  // index de prédiction disponible (cf. loadAndDisplaySim) pour afficher
  // la simulation complète d'entrée de jeu.
  const [activeStep, setActiveStep]         = useState(-1);
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [loadingPolygonsId, setLoadingPolygonsId] = useState(null);

  // ── Filtres indépendants ─────────────────────────────────────────────
  const [teamFilters, setTeamFilters] = useState(DEFAULT_FILTERS);
  const [myFilters, setMyFilters]     = useState(DEFAULT_FILTERS);

  const polygonDataRef    = useRef(null);
  const statsRequestedRef = useRef(new Set()); // évite les doublons de fetch stats
  const bulkRequestedRef  = useRef(new Set()); // évite les doublons de fetch bulk

  const teamFiltersRef        = useRef(teamFilters);
  const myFiltersRef          = useRef(myFilters);
  const teamFiltersVersionRef = useRef(0);
  const myFiltersVersionRef   = useRef(0);

  // Simple synchro de la ref avec le dernier state — AUCUN effet de bord ici
  // (pas d'incrément de version), donc totalement inoffensif même rejoué
  // plusieurs fois (StrictMode, remounts, etc.). Voir v15 ci-dessus.
  useEffect(() => {
    teamFiltersRef.current = teamFilters;
  }, [teamFilters]);

  useEffect(() => {
    myFiltersRef.current = myFilters;
  }, [myFilters]);

  // Dès qu'un filtre équipe change, le cache équipe est invalidé.
  useEffect(() => {
    setSimsByUser({});
    bulkRequestedRef.current.clear();
  }, [teamFilters.reference, teamFilters.dateFrom, teamFilters.dateTo]);

  // Dès qu'un filtre "mon historique" change, le cache "mon historique" est invalidé.
  useEffect(() => {
    setMySimsByUser({});
  }, [myFilters.reference, myFilters.dateFrom, myFilters.dateTo]);

  // statsByUser n'est JAMAIS touché par les filtres → les stats du haut restent stables.

  // ── Filtres équipe : la version est incrémentée ICI, de façon synchrone,
  // uniquement sur une vraie action utilisateur — jamais via un effet lié
  // au rendu (voir v15).
  const updateTeamFilters = useCallback((partial) => {
    teamFiltersVersionRef.current += 1;
    setTeamFilters((prev) => ({ ...prev, ...partial }));
  }, []);

  const resetTeamFilters = useCallback(() => {
    teamFiltersVersionRef.current += 1;
    setTeamFilters(DEFAULT_FILTERS);
  }, []);

  // ── Filtres "mon historique" : idem.
  const updateMyFilters = useCallback((partial) => {
    myFiltersVersionRef.current += 1;
    setMyFilters((prev) => ({ ...prev, ...partial }));
  }, []);

  const resetMyFilters = useCallback(() => {
    myFiltersVersionRef.current += 1;
    setMyFilters(DEFAULT_FILTERS);
  }, []);

  // ── Cache "stats" : toujours non-filtré, jamais invalidé ──
  const loadUserStats = useCallback((userId) => {
    const key = `u:${userId}`;
    if (statsRequestedRef.current.has(key)) return;
    statsRequestedRef.current.add(key);
    authService.getFiresByUser(userId, {})
      .then((sims) => setStatsByUser((prev) => ({ ...prev, [userId]: { sims } })))
      .catch((err) => {
        console.error('loadUserStats error:', err);
        statsRequestedRef.current.delete(key); // autorise un retry plus tard
      });
  }, []);

  const loadMyFiresStats = useCallback((userId) => {
    const key = `me:${userId}`;
    if (statsRequestedRef.current.has(key)) return;
    statsRequestedRef.current.add(key);
    authService.getMyFires({})
      .then((sims) => setStatsByUser((prev) => ({ ...prev, [userId]: { sims } })))
      .catch((err) => {
        console.error('loadMyFiresStats error:', err);
        statsRequestedRef.current.delete(key);
      });
  }, []);

  // ── Stats (bulk) : même rôle que loadUserStats mais pour N ids en un seul appel réseau.
  const loadTeamStatsBulk = useCallback(async (userIds) => {
    const ids = (userIds || [])
      .filter((id) => id !== undefined && id !== null)
      .filter((id) => !statsRequestedRef.current.has(`u:${id}`));
    if (!ids.length) return;

    ids.forEach((id) => statsRequestedRef.current.add(`u:${id}`));

    try {
      const grouped = await authService.getFiresByUsers(ids, {}); // pas de filtres
      setStatsByUser((prev) => {
        const next = { ...prev };
        ids.forEach((id) => {
          const sims = grouped?.[id] ?? grouped?.[String(id)] ?? [];
          next[id] = { sims };
        });
        return next;
      });
    } catch (err) {
      console.error('loadTeamStatsBulk error:', err);
      ids.forEach((id) => statsRequestedRef.current.delete(`u:${id}`)); // autorise un retry
    }
  }, []);

  // ── Équipe (id unique) : /fires_by_user/?user_id=X&reference=&date_from=&date_to= ─────
  const loadUserSims = useCallback(async (userId) => {
    loadUserStats(userId); // fire-and-forget, non filtré, pour les stats du haut

    const requestVersion = teamFiltersVersionRef.current;

    setSimsByUser((prev) => {
      if (prev[userId]?.sims?.length > 0) return prev;
      return { ...prev, [userId]: { loading: true, sims: [] } };
    });

    try {
      const sims = await authService.getFiresByUser(userId, teamFiltersRef.current);
      console.log(`loadUserSims(${userId}) → ${sims.length} simulations`, teamFiltersRef.current);

      if (requestVersion !== teamFiltersVersionRef.current) {
        console.log(`loadUserSims(${userId}) → réponse obsolète ignorée`);
        return;
      }

      setSimsByUser((prev) => ({ ...prev, [userId]: { loading: false, sims } }));
    } catch (err) {
      console.error('loadUserSims error:', err);
      if (requestVersion === teamFiltersVersionRef.current) {
        setSimsByUser((prev) => ({ ...prev, [userId]: { loading: false, sims: [] } }));
      }
    }
  }, [loadUserStats]);

  // ── Équipe (bulk) : un seul appel réseau pour N users ──
  const loadTeamSimsBulk = useCallback(async (userIds) => {
    const ids = (userIds || []).filter((id) => id !== undefined && id !== null);
    if (!ids.length) return;

    const bulkKey = `${teamFiltersVersionRef.current}:${[...ids].sort().join(',')}`;
    if (bulkRequestedRef.current.has(bulkKey)) return;
    bulkRequestedRef.current.add(bulkKey);

    const requestVersion = teamFiltersVersionRef.current;

    setSimsByUser((prev) => {
      const next = { ...prev };
      ids.forEach((id) => {
        if (!next[id]) next[id] = { loading: true, sims: [] };
      });
      return next;
    });

    try {
      const grouped = await authService.getFiresByUsers(ids, teamFiltersRef.current);
      console.log(`loadTeamSimsBulk(${ids.length} ids) → réponse reçue`, teamFiltersRef.current);

      if (requestVersion !== teamFiltersVersionRef.current) {
        console.log('loadTeamSimsBulk → réponse obsolète ignorée');
        return;
      }

      setSimsByUser((prev) => {
        const next = { ...prev };
        ids.forEach((id) => {
          const sims = grouped?.[id] ?? grouped?.[String(id)] ?? [];
          next[id] = { loading: false, sims };
        });
        return next;
      });

      loadTeamStatsBulk(ids);
    } catch (err) {
      console.error('loadTeamSimsBulk error:', err);
      bulkRequestedRef.current.delete(bulkKey); // autorise un retry
      if (requestVersion === teamFiltersVersionRef.current) {
        setSimsByUser((prev) => {
          const next = { ...prev };
          ids.forEach((id) => {
            if (next[id]?.loading) next[id] = { loading: false, sims: [] };
          });
          return next;
        });
      }
    }
  }, [loadTeamStatsBulk]);

  // ── Mon historique : /get_myfires/?reference=&date_from=&date_to= ─────────
  const loadMyFires = useCallback(async (userId) => {
    loadMyFiresStats(userId); // fire-and-forget, non filtré, pour les stats du haut

    const requestVersion = myFiltersVersionRef.current;

    setMySimsByUser((prev) => {
      if (prev[userId]?.sims?.length > 0) return prev;
      return { ...prev, [userId]: { loading: true, sims: [] } };
    });

    try {
      const sims = await authService.getMyFires(myFiltersRef.current);
      console.log(`loadMyFires(${userId}) → ${sims.length} simulations`, myFiltersRef.current);

      if (requestVersion !== myFiltersVersionRef.current) {
        console.log(`loadMyFires(${userId}) → réponse obsolète ignorée`);
        return;
      }

      setMySimsByUser((prev) => ({ ...prev, [userId]: { loading: false, sims } }));
    } catch (err) {
      console.error('loadMyFires error:', err);
      if (requestVersion === myFiltersVersionRef.current) {
        setMySimsByUser((prev) => ({ ...prev, [userId]: { loading: false, sims: [] } }));
      }
    }
  }, [loadMyFiresStats]);

  // ── Affichage cumulatif de la simulation en fonction du step ──
  // main + start toujours visibles ; predictions d'index <= step visibles.
  const handleStepChange = useCallback((step) => {
    setActiveStep(step);
    const map = mapRef?.current?.getMap?.();
    if (!map || !polygonDataRef.current) return;

    const mainPoly  = polygonDataRef.current.find((p) => p.type === 'main');
    const startPoly = polygonDataRef.current.find((p) => p.type === 'start');

    // Repère de fond permanent : jamais masqué, quel que soit le step actif.
    if (mainPoly) {
      const mainFillId    = `sim-main-${mainPoly.order}-fill`;
      const mainOutlineId = `sim-main-${mainPoly.order}-outline`;
      if (map.getLayer(mainFillId))    map.setPaintProperty(mainFillId,    'fill-opacity', 1);
      if (map.getLayer(mainOutlineId)) map.setPaintProperty(mainOutlineId, 'line-opacity', 0.7);
    }
    if (startPoly) {
      const startCircleId = `sim-start-${startPoly.order}-circle`;
      if (map.getLayer(startCircleId)) {
        map.setPaintProperty(startCircleId, 'circle-opacity', 1);
        map.setPaintProperty(startCircleId, 'circle-stroke-opacity', 1);
      }
    }

    // Cumulatif : tous les polygones de prédiction jusqu'au step courant
    // restent visibles (pas uniquement l'étape active).
    polygonDataRef.current
      .filter((p) => p.type === 'predictions') // déjà trié par order croissant
      .forEach((poly, i) => {
        const fillId    = `sim-pred-${poly.order}-fill`;
        const outlineId = `sim-pred-${poly.order}-outline`;
        const visible = i <= step;
        if (map.getLayer(fillId))    map.setPaintProperty(fillId,    'fill-opacity', visible ? 1 : 0);
        if (map.getLayer(outlineId)) map.setPaintProperty(outlineId, 'line-opacity', visible ? 0.7 : 0);
      });
  }, [mapRef]);

  const loadAndDisplaySim = useCallback(async (simulation) => {
    const map = mapRef?.current?.getMap?.();
    if (!map) return;
    const fireId = simulation.fire?.id;
    if (!fireId) return;

    setLoadingPolygonsId(fireId);

    clearSimLayers(map);
    setSelectedSim(simulation);

    try {
      const polygonsRaw = await authService.getFirePolygons(fireId);
      console.log(`loadAndDisplaySim(${fireId}) → ${polygonsRaw.length} polygones`);

      // ── Ordonnancement garanti : le "premier contour" (main, associé à
      // date_first_outline) sert de socle, puis le point de départ (start),
      // puis les prédictions strictement triées par `order` croissant
      // (0, 1, 2…). Sans ce tri, le timeline slider de SimulationInfoOverlay
      // et le calque mis en avant à chaque étape (cf. handleStepChange plus
      // bas) ne correspondaient pas forcément à l'ordre reçu du backend, qui
      // n'est pas garanti.
      const mainPoly  = polygonsRaw.find((p) => p.type === 'main');
      const startPoly = polygonsRaw.find((p) => p.type === 'start');
      const predictionPolys = polygonsRaw
        .filter((p) => p.type === 'predictions')
        .sort((a, b) => Number(a.order) - Number(b.order));

      // Ordre LOGIQUE (pour le slider / l'export / handleStepChange) :
      // main, start, puis prédictions triées.
      const orderedPolygons = [
        ...(mainPoly ? [mainPoly] : []),
        ...(startPoly ? [startPoly] : []),
        ...predictionPolys,
      ];

      polygonDataRef.current = orderedPolygons;

      const allCollections = [];

      // ── Ordre de DESSIN distinct de l'ordre logique (orderedPolygons) ──
      // Les prédictions grandissent progressivement et finissent par englober
      // géographiquement `main`/`start`. On les dessine donc EN PREMIER, et
      // `main` puis `start` EN DERNIER, pour qu'ils restent visibles au-dessus
      // en tant que repère permanent (cf. handleStepChange, qui les garde
      // toujours à pleine opacité).
      const drawOrder = [
        ...predictionPolys,
        ...(mainPoly ? [mainPoly] : []),
        ...(startPoly ? [startPoly] : []),
      ];

      drawOrder.forEach((poly) => {
        const { type, order } = poly;
        poly.features?.forEach((fc) => {
          const features = fc?.features ?? [];
          if (!features.length) return;
          const geojson = { type: 'FeatureCollection', features };
          allCollections.push(geojson);

          if (type === 'main') {
            addPolygonLayer(map, `sim-main-${order}`, geojson, FIRST_OUTLINE_FILL, FIRST_OUTLINE_OUTLINE);
          } else if (type === 'start') {
            addPointLayer(map, `sim-start-${order}`, geojson);
          } else if (type === 'predictions') {
            const colorIdx = Math.min(Number(order) - 1, PREDICTION_COLORS.length - 1);
            addPolygonLayer(
              map, `sim-pred-${order}`, geojson,
              PREDICTION_COLORS[Math.max(0, colorIdx)],
              `rgba(255,150,50,${0.3 + colorIdx * 0.05})`
            );
          }
        });
      });

      const bounds = getBounds(allCollections);
      if (bounds) map.fitBounds(bounds, { padding: 60, duration: 1200, maxZoom: 14 });

      // ── État initial = simulation COMPLÈTE ──
      // Au lieu de partir de -1 (main + start seuls), on affiche d'emblée
      // le dernier polygone de prédiction disponible, pour que la
      // simulation apparaisse entière dès la sélection. L'utilisateur peut
      // ensuite reculer dans le slider pour revoir la progression depuis
      // le début. Si aucune prédiction n'existe, on retombe sur -1 (main +
      // start seuls, rien d'autre à afficher).
      const initialStep = predictionPolys.length > 0 ? predictionPolys.length - 1 : -1;
      handleStepChange(initialStep);

    } catch (err) {
      console.error('loadAndDisplaySim error:', err);
    } finally {
      setLoadingPolygonsId(null);
    }
  }, [mapRef, handleStepChange]);

  const handleSelectSim = useCallback((simulation) => {
    if (selectedSim?.fire?.id === simulation?.fire?.id) {
      clearSimulation();
      return;
    }
    loadAndDisplaySim(simulation);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSim, loadAndDisplaySim]);

  const clearSimulation = useCallback(() => {
    setSelectedSim(null);
    setActiveStep(-1);
    polygonDataRef.current = null;
    clearSimLayers(mapRef?.current?.getMap?.());
  }, [mapRef]);

  // ── Export GeoJSON de la simulation sélectionnée ──────────────────────────
  // Nom du fichier :
  //   simulation-<nom simulation>-<utilisateur>-<JJ-MM-AAAA>.geojson
  const exportSimulationGeoJSON = useCallback(() => {
    if (!selectedSim || !polygonDataRef.current) return false;

    const features = [];
    polygonDataRef.current.forEach((poly) => {
      const { type, order } = poly;
      (poly.features || []).forEach((fc) => {
        (fc?.features || []).forEach((feature) => {
          features.push({
            ...feature,
            properties: {
              ...(feature.properties || {}),
              sim_layer_type: type,
              sim_layer_order: order,
            },
          });
        });
      });
    });

    if (!features.length) return false;

    const fireRef   = selectedSim.fire?.reference ?? 'simulation';
    const createdAt = selectedSim.fire?.created_at;
    const author    = getSimulationAuthor(selectedSim);

    // Date de la simulation (et non la date d'export), repli sur aujourd'hui
    const fileDate = formatDateFR(createdAt, '-');

    const fileName = [
      'simulation',
      slugifyName(fireRef),
      slugifyName(author),
      fileDate,
    ].filter(Boolean).join('-');

    const geojson = {
      type: 'FeatureCollection',
      metadata: {
        fire_id: fireRef,
        launched_by: author || null,
        simulation_date: formatDateFR(createdAt, '/'),
        created_at: createdAt ?? null,
        exported_at: new Date().toISOString(),
        total_features: features.length,
        source: 'SecuFire Admin Dashboard',
      },
      features,
    };

    triggerGeoJSONDownload(geojson, `${fileName}.geojson`);
    return true;
  }, [selectedSim]);

  // totalSteps ne compte QUE les prédictions — main/start ne sont plus des
  // "étapes" du slider mais un repère de fond permanent (voir v14 ci-dessus).
  const totalSteps = polygonDataRef.current
    ? polygonDataRef.current.filter((p) => p.type === 'predictions').length
    : 0;

  return {
    // caches
    simsByUser,     // équipe
    mySimsByUser,   // mon historique
    statsByUser,

    // sélection / carte
    selectedSim,
    activeStep,
    totalSteps,
    expandedUserId,
    loadingPolygonsId,

    // loaders
    loadUserSims,      // équipe (id unique)
    loadTeamSimsBulk,  // équipe (bulk, pour le préchargement d'élagage par filtre)
    loadMyFires,        // mon historique
    handleSelectSim,
    handleStepChange,
    clearSimulation,
    setExpandedUserId,
    exportSimulationGeoJSON,

    // filtres équipe
    teamFilters,
    updateTeamFilters,
    resetTeamFilters,

    // filtres mon historique
    myFilters,
    updateMyFilters,
    resetMyFilters,
  };
};

export default useSimulations;